import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type NotificationType = 'deadline' | 'risk' | 'update' | 'mention' | 'task' | 'milestone';
export type NotificationPriority = 'critical' | 'high' | 'medium' | 'low';

export interface AppNotification {
  id: string;
  tenant_id: string;
  user_id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  body: string | null;
  link_view: string | null;
  link_ref_id: string | null;
  dedupe_key: string;
  read: boolean;
  dismissed: boolean;
  created_at: string;
}

export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_id', user.id)
      .eq('dismissed', false)
      .order('created_at', { ascending: false })
      .limit(100);

    if (error) {
      console.error('Failed to load notifications:', error);
    } else {
      setNotifications((data as AppNotification[]) || []);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
    const channel = supabase
      .channel('notifications')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications' }, load)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [load]);

  const markRead = useCallback(async (id: string) => {
    const { error } = await supabase.from('notifications').update({ read: true }).eq('id', id);
    if (error) console.error('Failed to mark notification read:', error);
  }, []);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false);
    if (error) console.error('Failed to mark all notifications read:', error);
  }, [user]);

  const dismiss = useCallback(async (id: string) => {
    const { error } = await supabase.from('notifications').update({ dismissed: true }).eq('id', id);
    if (error) console.error('Failed to dismiss notification:', error);
  }, []);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);
  const criticalCount = useMemo(
    () => notifications.filter((n) => !n.read && n.priority === 'critical').length,
    [notifications],
  );

  return { notifications, loading, markRead, markAllRead, dismiss, unreadCount, criticalCount };
};
